import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { TableStore } from 'shared/ui/TableNew/store/tableStore'
import { RequestLoadData } from 'shared/api/lib'
import { Invite } from 'entities/Users/model/Invite'
import { RowInvite } from 'entities/Users/model/Table/RowInvite'
import { type IParamsOrganizationInvites, type IResponseInvite } from 'entities/Users/api/types'
import { UsersApi } from 'entities/Users'
import { tableLimitStore } from 'entities/TableLimit'

export class PendingMembersStore {
  private readonly _tableStore: TableStore<RowInvite>
  private _itemsMap: Map<string, Invite> = new Map()
  private _search = ''
  private _scrollToTopTrigger = ''
  private _disposeParamsGetItems: IReactionDisposer | null = null
  private _disposeParamsWithoutPage: IReactionDisposer | null = null

  constructor() {
    this._tableStore = new TableStore({
      key: 'pendingMembersTable',
      element: 'invite',
      limit: 10,
      page: 1,
      getLimit: () => tableLimitStore.getLimit('pending-members-table'),
      setLimit: (value) => tableLimitStore.setLimit('pending-members-table', value),
      useColumnsCache: true,
    })
    makeAutoObservable(this)
  }

  init = () => {
    this.loadData()
    this.reactionParamsWithoutPage()
    this.reactionParamsGetItems()
  }

  reactionParamsWithoutPage = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsWithoutPage = reaction(
      () => this.paramsWithoutPage,
      () => {
        this._tableStore.setLoading(true)
        this._tableStore.setPage(1)
      }
    )
  }

  reactionParamsGetItems = () => {
    this._disposeParamsGetItems?.()
    this._disposeParamsGetItems = reaction(
      () => this.paramsGetItems,
      () => this._requestLoadData.debounceLoadData()
    )
  }

  private _setItem = (data: IResponseInvite) => {
    runInAction(() => {
      this._itemsMap.set(data.id, new Invite(data))
    })
  }

  setSearch = (value: string) => {
    this._search = value
  }

  private _requestLoadData = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      runInAction(() => {
        this._tableStore.setLoading(true)
      })

      this._tableStore.setBulkTotal(0)
      this._tableStore.bulkStore.resetBulkAll()

      const { data } = await UsersApi.getOrganizationPendingInvitesList(this.paramsGetItems, {
        cancelToken: cancelRequest.token,
      })

      runInAction(() => {
        this._itemsMap.clear()
        data.invitations.data.forEach((item) => {
          this._setItem(item)
        })
        this._tableStore.setRows(this.itemsRows)
        this._tableStore.setTotal(data.invitations.meta.total)
        this._scrollToTopTrigger = nanoid()
      })
    },
    finallyCallback: () => {
      runInAction(() => {
        this._tableStore.setLoading(false)
      })
    },
  })

  loadData = async () => {
    await this._requestLoadData.loadData()
  }

  get items() {
    return Array.from(this._itemsMap.values())
  }

  get paramsWithoutPage(): Omit<IParamsOrganizationInvites, 'page'> {
    return {
      per_page: this._tableStore.limit,
      search: this._search || undefined,
    }
  }

  get paramsGetItems(): IParamsOrganizationInvites {
    return {
      page: this._tableStore.page,
      ...this.paramsWithoutPage,
    }
  }

  get itemsRows() {
    return this.items.map((item) => new RowInvite(item))
  }

  get tableStore() {
    return this._tableStore
  }

  get loading() {
    return this._tableStore.loading
  }

  get search() {
    return this._search
  }

  get scrollToTopTrigger() {
    return this._scrollToTopTrigger
  }

  get total() {
    return this._tableStore.total
  }

  dispose = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsGetItems?.()
    this._requestLoadData.dispose()
  }
}
